// PhilosophyFlowSceneDOM.js - Template-based Philosophy Flow
// Uses glass morphism template system instead of old Phaser UI

import BaseMenuScene from "./BaseMenuScene.js";
import { auth } from "../firebaseConfig.js";
import { createTemplateHeader, loadTemplateHeaderCSS, bindTemplateHeaderEvents } from "../components/SharedTemplateHeader.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";

export default class PhilosophyFlowSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "PhilosophyFlowSceneDOM" });
        this.domElement = null;
        this.isMobile = window.MobileDetectionService?.isMobile() || window.innerWidth <= 768;
        this.currentStep = 1;
        this.selectedFeeling = null;
        this.selectedColor = null;
    }

    async create() {
        console.log('🎭 PhilosophyFlowSceneDOM: Creating template-based Philosophy Flow');
        
        // Load template CSS
        loadTemplateHeaderCSS();
        
        // Load Philosophy Flow specific CSS
        if (!document.getElementById('philosophy-flow-specific-css')) {
            const link = document.createElement('link');
            link.id = 'philosophy-flow-specific-css';
            link.rel = 'stylesheet';
            link.href = '/philosophy-flow-template.css?v=' + Date.now();
            document.head.appendChild(link);
            console.log('🎨 PhilosophyFlowSceneDOM: Specific template CSS loaded');
        }
        
        // Create the interface
        this.createPhilosophyInterface();
        
        // Bind events
        bindTemplateHeaderEvents(this);
        this.setupEventListeners();
        
        // Register with overlay manager
        templateOverlayManager.registerOverlay('philosophy-flow-scene-overlay', 'PhilosophyFlowSceneDOM');
        
        this.sceneReady = true;
    }

    createPhilosophyInterface() {
        // Remove existing overlay
        const existingOverlay = document.getElementById('philosophy-flow-scene-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'philosophy-flow-scene-overlay';
        overlay.className = 'template-overlay glass-morphism-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            z-index: 10000;
            overflow-y: auto;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
        `;
        
        overlay.innerHTML = this.isMobile ? this.createMobilePhilosophyHTML() : this.createDesktopPhilosophyHTML();
        
        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        console.log('🎭 PhilosophyFlowSceneDOM: Template interface created');
    }

    createDesktopPhilosophyHTML() {
        return `
            ${createTemplateHeader('Philosophy Flow', ['Home', 'Dashboard', 'Philosophy Flow'])}
            
            <div class="app-container">
                <!-- Sidebar Navigation -->
                <aside class="sidebar">
                    <div class="sidebar-header">
                        <div class="logo">
                            <div class="logo-icon">🎨</div>
                            <div class="logo-text">
                                <div>Philosophy Flow</div>
                                <div class="logo-subtitle">Strategic Thinking</div>
                            </div>
                        </div>
                    </div>
                    
                    <nav class="sidebar-nav">
                        <div class="progress-section">
                            <div class="progress-title">Step ${this.currentStep} of 6</div>
                            <div class="progress-bar">
                                <div class="progress-fill" style="width: ${(this.currentStep / 6) * 100}%"></div>
                            </div>
                        </div>
                    </nav>
                </aside>
                
                <!-- Main Content -->
                <main class="main-content">
                    <div class="philosophy-content">
                        <div class="step-header">
                            <h1 class="step-title">Explore Your Inner Landscape</h1>
                            <p class="step-description">Choose the category that best represents your current emotional territory</p>
                        </div>
                        
                        <div class="philosophy-step-content" id="philosophy-step-content">
                            ${this.createStepContent()}
                        </div>
                        
                        <div class="step-actions">
                            <button class="btn-secondary" data-action="back">← Back</button>
                            <button class="btn-primary" data-action="continue">Continue →</button>
                        </div>
                    </div>
                </main>
            </div>
        `;
    }

    createMobilePhilosophyHTML() {
        return `
            ${createTemplateHeader('Philosophy Flow', ['Home', 'Dashboard', 'Philosophy Flow'])}
            
            <div class="mobile-container">
                <header class="mobile-header">
                    <div class="progress-indicator">
                        <span class="step-counter">Step ${this.currentStep} of 6</span>
                        <div class="progress-bar">
                            <div class="progress-fill" style="width: ${(this.currentStep / 6) * 100}%"></div>
                        </div>
                    </div>
                </header>
                
                <main class="mobile-content">
                    <div class="step-header">
                        <h1 class="step-title">Explore Your Inner Landscape</h1>
                        <p class="step-description">Choose the category that best represents your current emotional territory</p>
                    </div>
                    
                    <div class="philosophy-step-content" id="philosophy-step-content">
                        ${this.createStepContent()}
                    </div>
                    
                    <div class="mobile-actions">
                        <button class="btn-secondary" data-action="back">← Back</button>
                        <button class="btn-primary" data-action="continue">Continue →</button>
                    </div>
                </main>
            </div>
        `;
    }

    createStepContent() {
        // Create step content with glass morphism styling
        return `
            <div class="emotion-categories">
                <div class="category-grid">
                    <div class="category-card" data-category="cognitive">
                        <div class="category-icon">🧠</div>
                        <h3 class="category-title">Cognitive States</h3>
                        <p class="category-description">Mental clarity, thinking patterns, and cognitive processes</p>
                        <div class="category-tags">Analytical • Creative • Focused • Curious</div>
                    </div>
                    
                    <div class="category-card" data-category="uplifting">
                        <div class="category-icon">✨</div>
                        <h3 class="category-title">Uplifting States</h3>
                        <p class="category-description">Positive emotions and elevated feelings</p>
                        <div class="category-tags">Joyful • Calm • Hopeful • Excited</div>
                    </div>
                    
                    <div class="category-card" data-category="challenging">
                        <div class="category-icon">⚡</div>
                        <h3 class="category-title">Challenging States</h3>
                        <p class="category-description">Difficult emotions that signal important needs</p>
                        <div class="category-tags">Angry • Frustrated • Worried • Disappointed</div>
                    </div>
                    
                    <div class="category-card" data-category="heavy">
                        <div class="category-icon">🌊</div>
                        <h3 class="category-title">Heavy States</h3>
                        <p class="category-description">Deep, intense emotions requiring gentle attention</p>
                        <div class="category-tags">Sad • Lonely • Empty • Lost</div>
                    </div>
                    
                    <div class="category-card" data-category="energy">
                        <div class="category-icon">🔥</div>
                        <h3 class="category-title">Energy States</h3>
                        <p class="category-description">Physical and emotional energy levels</p>
                        <div class="category-tags">Energized • Balanced • Tired • Drained</div>
                    </div>
                    
                    <div class="category-card" data-category="reflective">
                        <div class="category-icon">🔮</div>
                        <h3 class="category-title">Reflective States</h3>
                        <p class="category-description">Contemplative and introspective moods</p>
                        <div class="category-tags">Philosophical • Searching • Meditative • Contemplative</div>
                    </div>
                </div>
            </div>
        `;
    }

    setupEventListeners() {
        if (!this.domElement) return;
        
        // Category card selection
        this.domElement.querySelectorAll('.category-card').forEach(card => {
            card.addEventListener('click', (e) => {
                const category = e.currentTarget.dataset.category;
                this.selectCategory(category);
            });
        });
        
        // Action buttons
        this.domElement.querySelectorAll('[data-action]').forEach(button => {
            button.addEventListener('click', (e) => {
                const action = e.currentTarget.dataset.action;
                this.handleAction(action);
            });
        });
    }

    selectCategory(category) {
        console.log('🎭 PhilosophyFlowSceneDOM: Category selected:', category);
        
        // Update UI to show selection
        this.domElement.querySelectorAll('.category-card').forEach(card => {
            card.classList.remove('selected');
        });
        
        const selectedCard = this.domElement.querySelector(`[data-category="${category}"]`);
        if (selectedCard) {
            selectedCard.classList.add('selected');
        }
        
        this.selectedCategory = category;
        
        // Show success notification
        if (window.toastManager) {
            window.toastManager.success('Category Selected', `You selected ${category} states for reflection.`);
        }
    }

    handleAction(action) {
        console.log('🎭 PhilosophyFlowSceneDOM: Handling action:', action);
        
        switch (action) {
            case 'back':
            case 'dashboard':
                templateOverlayManager.prepareForTransition('AuthenticatedMenuSceneDOM');
                this.startScene('AuthenticatedMenuSceneDOM');
                break;
            case 'continue':
                if (this.selectedCategory) {
                    this.nextStep();
                } else {
                    if (window.toastManager) {
                        window.toastManager.warning('Selection Required', 'Please select a category before continuing.');
                    }
                }
                break;
        }
    }

    nextStep() {
        console.log('🎭 PhilosophyFlowSceneDOM: Proceeding to next step');
        
        if (window.toastManager) {
            window.toastManager.info('Philosophy Complete', 'Your philosophical reflection has been recorded.');
        }
        
        // Return to dashboard after brief delay
        setTimeout(() => {
            templateOverlayManager.prepareForTransition('AuthenticatedMenuSceneDOM');
            this.startScene('AuthenticatedMenuSceneDOM');
        }, 2000);
    }

    shutdown() {
        console.log('🎭 PhilosophyFlowSceneDOM: Shutting down...');
        
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        // Clean up overlay
        const overlay = document.getElementById('philosophy-flow-scene-overlay');
        if (overlay) {
            overlay.remove();
        }
        
        super.shutdown();
    }
}